import { Token, Percent, JSBI } from '@uniswap/sdk'
import { getContract } from './index'

// Standard tax token ABI - these are common methods found in most tax tokens
const TAX_TOKEN_ABI = [
  // Primary tax methods from TaxToken.sol - these should be checked first
  'function contractName() view returns (string)',
  'function _totalTaxIfBuying() view returns (uint256)',
  'function _totalTaxIfSelling() view returns (uint256)',

  // Standard fee getters
  'function buyLiquidityFee() view returns (uint256)',
  'function buyMarketingFee() view returns (uint256)',
  'function buyTeamFee() view returns (uint256)',
  'function buyDestroyFee() view returns (uint256)',
  'function sellLiquidityFee() view returns (uint256)',
  'function sellMarketingFee() view returns (uint256)',
  'function sellTeamFee() view returns (uint256)',
  'function sellDestroyFee() view returns (uint256)',

  // Alternative naming patterns
  'function _buyLiquidityFee() view returns (uint256)',
  'function _buyMarketingFee() view returns (uint256)',
  'function _buyTeamFee() view returns (uint256)',
  'function _buyDestroyFee() view returns (uint256)',
  'function _sellLiquidityFee() view returns (uint256)',
  'function _sellMarketingFee() view returns (uint256)',
  'function _sellTeamFee() view returns (uint256)',
  'function _sellDestroyFee() view returns (uint256)',

  // Total fee getters (some tokens have these)
  'function buyTotalFees() view returns (uint256)',
  'function sellTotalFees() view returns (uint256)',
  'function totalBuyFee() view returns (uint256)',
  'function totalSellFee() view returns (uint256)',

  // Fee checks
  'function isExcludedFromFee(address) view returns (bool)',
  'function isMarketPair(address) view returns (bool)'
]

// Tax information cache
interface TaxInfo {
  buyTaxPercent: Percent
  sellTaxPercent: Percent
  lastUpdated: number
  isDetected: boolean
}

const TAX_CACHE: { [address: string]: TaxInfo } = {}
const CACHE_DURATION = 30 * 60 * 1000 // 30 minute

// Addresses that should never be considered tax tokens
const TAX_TOKEN_EXCLUSIONS: Set<string> = new Set([
  '0x82f9d23cB62Ec0016109B7C4b8dB34890FdBA0F0'.toLowerCase(), // MUD token
  '0xB82Db19f90daA3F40F25f7aC71786E7268139b0E'.toLowerCase() // USDT token
])

// Pre-populate known tax tokens to avoid async issues
const KNOWN_TAX_TOKENS: { [address: string]: { buyTax: number; sellTax: number } } = {
  '0x6469f5efa500c4b87aac3125ee728c6a187563c4': { buyTax: 2000, sellTax: 1000 } // Your TT token - 20% buy, 10% sell
}

// Initialize known tax tokens in cache
Object.entries(KNOWN_TAX_TOKENS).forEach(([address, { buyTax, sellTax }]) => {
  TAX_CACHE[address.toLowerCase()] = {
    buyTaxPercent: new Percent(JSBI.BigInt(buyTax), JSBI.BigInt(10000)),
    sellTaxPercent: new Percent(JSBI.BigInt(sellTax), JSBI.BigInt(10000)),
    lastUpdated: Date.now(),
    isDetected: true
  }
})

/**
 * Attempt to detect if a token has tax functionality by checking common method signatures
 */
export async function detectTaxToken(token: Token, library: any): Promise<TaxInfo> {
  const cacheKey = token.address.toLowerCase()

  // Check exclusion list first - these addresses should never be considered tax tokens
  if (TAX_TOKEN_EXCLUSIONS.has(cacheKey)) {
    const excludedInfo: TaxInfo = {
      buyTaxPercent: new Percent(JSBI.BigInt(0), JSBI.BigInt(10000)),
      sellTaxPercent: new Percent(JSBI.BigInt(0), JSBI.BigInt(10000)),
      lastUpdated: Date.now(),
      isDetected: false
    }
    TAX_CACHE[cacheKey] = excludedInfo
    return excludedInfo
  }

  const cached = TAX_CACHE[cacheKey]

  // Return cached result if still valid
  if (cached && Date.now() - cached.lastUpdated < CACHE_DURATION) {
    return cached
  }

  try {
    const contract = getContract(token.address, TAX_TOKEN_ABI, library)

    let buyTax = 0
    let sellTax = 0
    let isDetected = false

    // Try the primary TaxToken.sol methods first (most reliable)
    try {
      const contractName = await contract.contractName()
      if (contractName !== 'TaxToken') {
        const excludedInfo: TaxInfo = {
          buyTaxPercent: new Percent(JSBI.BigInt(0), JSBI.BigInt(10000)),
          sellTaxPercent: new Percent(JSBI.BigInt(0), JSBI.BigInt(10000)),
          lastUpdated: Date.now(),
          isDetected: false
        }
        TAX_CACHE[cacheKey] = excludedInfo
        return excludedInfo
      }

      const buyTaxResult = await contract._totalTaxIfBuying()
      const sellTaxResult = await contract._totalTaxIfSelling()

      buyTax = buyTaxResult.toNumber()
      sellTax = sellTaxResult.toNumber()
      isDetected = true

      console.log(`Using TaxToken.sol methods for ${token.symbol}: buy=${buyTax}, sell=${sellTax}`)
    } catch (error) {
      console.log(`TaxToken.sol methods not available for ${token.symbol}, trying fallback methods`)
    }

    const taxInfo: TaxInfo = {
      buyTaxPercent: new Percent(JSBI.BigInt(buyTax), JSBI.BigInt(10000)),
      sellTaxPercent: new Percent(JSBI.BigInt(sellTax), JSBI.BigInt(10000)),
      lastUpdated: Date.now(),
      isDetected
    }

    // Cache the result
    TAX_CACHE[cacheKey] = taxInfo

    console.log(`Tax detection for ${token.symbol} (${token.address}):`, {
      isDetected,
      buyTax: `${(buyTax / 100).toFixed(2)}%`,
      sellTax: `${(sellTax / 100).toFixed(2)}%`,
      buyTaxBasisPoints: buyTax,
      sellTaxBasisPoints: sellTax
    })

    return taxInfo
  } catch (error) {
    console.warn(`Failed to detect tax for token ${token.symbol}:`, error)

    // Cache negative result to avoid repeated calls
    const taxInfo: TaxInfo = {
      buyTaxPercent: new Percent(JSBI.BigInt(0), JSBI.BigInt(10000)),
      sellTaxPercent: new Percent(JSBI.BigInt(0), JSBI.BigInt(10000)),
      lastUpdated: Date.now(),
      isDetected: false
    }

    TAX_CACHE[cacheKey] = taxInfo
    return taxInfo
  }
}

/**
 * Check if a token is a tax token (uses cache)
 */
export function isTaxToken(token: Token): boolean {
  const cacheKey = token.address.toLowerCase()
  const cached = TAX_CACHE[cacheKey]

  if (!cached) {
    console.log(`No cache for isTaxToken check: ${token.symbol} (${token.address})`)
    return false
  }

  const hasTax =
    cached.isDetected &&
    (cached.buyTaxPercent.greaterThan(new Percent(JSBI.BigInt(0), JSBI.BigInt(10000))) ||
      cached.sellTaxPercent.greaterThan(new Percent(JSBI.BigInt(0), JSBI.BigInt(10000))))

  // console.log(
  //   `isTaxToken for ${token.symbol}: ${hasTax} (detected: ${cached.isDetected}, buy: ${cached.buyTaxPercent.toFixed(
  //     2
  //   )}%, sell: ${cached.sellTaxPercent.toFixed(2)}%)`
  // )

  return hasTax
}

/**
 * Get buy tax for a token (from cache)
 */
export function getTotalBuyTax(token: Token): Percent {
  const cacheKey = token.address.toLowerCase()
  const cached = TAX_CACHE[cacheKey]

  if (!cached) {
    // console.log(`No tax cache found for ${token.symbol} (${token.address})`)
    return new Percent(JSBI.BigInt(0), JSBI.BigInt(10000))
  }

  // console.log(`Getting buy tax for ${token.symbol}: ${cached.buyTaxPercent.toFixed(2)}%`)
  return cached.buyTaxPercent
}

/**
 * Get sell tax for a token (from cache)
 */
export function getTotalSellTax(token: Token): Percent {
  const cacheKey = token.address.toLowerCase()
  const cached = TAX_CACHE[cacheKey]

  if (!cached) {
    console.log(`No tax cache found for ${token.symbol} (${token.address})`)
    return new Percent(JSBI.BigInt(0), JSBI.BigInt(10000))
  }

  console.log(`Getting sell tax for ${token.symbol}: ${cached.sellTaxPercent.toFixed(2)}%`)
  return cached.sellTaxPercent
}

/**
 * Force refresh tax information for a token
 */
export async function refreshTaxInfo(token: Token, library: any): Promise<TaxInfo> {
  const cacheKey = token.address.toLowerCase()
  delete TAX_CACHE[cacheKey]
  return detectTaxToken(token, library)
}

/**
 * Clear all cached tax information
 */
export function clearTaxCache(): void {
  Object.keys(TAX_CACHE).forEach(key => delete TAX_CACHE[key])
}

/**
 * Get cached tax info (for debugging)
 */
export function getTaxCache(): { [address: string]: TaxInfo } {
  return { ...TAX_CACHE }
}
